/** Типизированные вызовы API. Типы домена — из @sync-tracker/shared. */
import type {
  BoardColumn,
  ChatCategory,
  ChatDto,
  ChatMessageDto,
  Capture,
  ColumnAccess,
  ColumnGroup,
  ColumnRules,
  Employee,
  EngineStatus,
  Resolution,
  SessionUser,
  Settings,
  SettingsSection,
  TicketDto,
  TicketEventDto,
  TicketMarks,
  TicketWatcher,
  MentionDto,
  OutgoingReplyDto,
  UserPrefs,
  UserRole,
  GroupRequestStatus,
  ResponseBucketKey,
} from '@sync-tracker/shared';
import { http } from './http';

export interface Department {
  key: string;
  title: string;
  color: string;
  sort: number;
}

export interface TimerLevel {
  key: string;
  label: string;
  afterSec: number;
  color: string;
}

export interface Bootstrap {
  columns: BoardColumn[];
  /** Колонки доски пользователя (решает сервер). */
  boardColumnKeys?: string[];
  /** Группы колонок пользователя (у администратора — все), по порядку. */
  columnGroups?: ColumnGroup[];
  categories: ChatCategory[];
  resolutions: Resolution[];
  departments: Department[];
  levels: TimerLevel[];
  timers: Settings['timers'];
  general: Settings['general'];
  /** Нерабочее время: колонка «Пропущенные» и отсчёт таймера её карточек. */
  offHours?: { missedColumnKey: string | null; timerFromWorkStart: boolean };
  employees: Pick<Employee, 'id' | 'username' | 'displayName' | 'department' | 'kind'>[];
  engine: (EngineStatus & { stats?: Record<string, number> }) | null;
}

export interface ColumnFull extends BoardColumn {
  rules: ColumnRules;
}

export interface PersonalColumn {
  key: string;
  title: string;
  color: string;
  sort: number;
  enabled: boolean;
  capture: Capture;
  ownerUserId: number;
  /** «Учитывать в статистике»; менять может только администратор. */
  statsEnabled?: boolean;
}
/** Сотрудник, к которому привязан пользователь, — «я» для личных колонок. */
export interface PersonalOwner {
  id: number;
  name: string;
  username: string;
}
export type PersonalColumnInput = Omit<PersonalColumn, 'key' | 'ownerUserId'>;

export interface ColumnGroupFull extends ColumnGroup {
  userIds: number[];
}
export type ColumnGroupInput = Omit<ColumnGroupFull, 'id'>;

export interface AutoreplyLogItem {
  id: string;
  chatId: string;
  chatTitle: string | null;
  status: 'pending' | 'sending' | 'sent' | 'error' | 'expired';
  error: string | null;
  ticketId: string | null;
  createdAt: string;
  sentAt: string | null;
}

export type MatchOutcome = 'pending' | 'ticket' | 'appended' | 'closed_early' | 'half' | 'expired' | 'excluded';

/** Статистика личной колонки за период. */
export interface PersonalColumnStats {
  period: { from: string; to: string; timezone: string };
  matches: {
    total: number;
    tickets: number;
    appended: number;
    pending: number;
    closedEarly: number;
    halfWaiting: number;
    unconfirmed: number;
    excluded: number;
    bySender: { sender: 'client' | 'staff'; tab: string; count: number }[];
    byDay: { day: string; count: number; tickets: number }[];
    byHour: { hour: number; count: number }[];
  };
  tickets: {
    total: number;
    open: number;
    closed: number;
    closedByWord: number;
    medianCloseSec: number | null;
    avgCloseSec: number | null;
    medianResponseSec: number | null;
  };
  closers: { name: string; count: number; medianCloseSec: number | null }[];
  /** Динамика качества: закрытые по периодам (день / неделя / месяц) и статусам. */
  quality: {
    bucket: 'day' | 'week' | 'month';
    periods: { start: string; closed: number; byResolution: Record<string, number> }[];
  };
  recent: {
    id: string;
    at: string;
    chatId: string;
    chatTitle: string | null;
    sender: string | null;
    senderKind: 'client' | 'staff';
    tab: string;
    hit: string | null;
    text: string | null;
    outcome: MatchOutcome;
    /** Ключ колонки-исключения, из-за которой совпадение пропущено. */
    excludedBy: string | null;
    ticketId: string | null;
    closedAt: string | null;
    closedBy: string | null;
  }[];
}

/** Доступ со стороны колонки: в каких группах она и кому выдана точечно. */
export interface ColumnAccessByColumn {
  groupIds: number[];
  userIds: number[];
}

export type EmployeeFull = Employee & { note: string | null };

export interface AppUser {
  id: number;
  login: string;
  displayName: string;
  role: UserRole;
  active: boolean;
  employeeId: number | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AppUserWithAccess extends AppUser {
  access: ColumnAccess;
  /** Личные колонки пользователя (ключи). */
  ownColumns: string[];
}

export interface AuditItem {
  id: string;
  at: string;
  userId: number | null;
  userLogin: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  before: unknown;
  after: unknown;
  meta: unknown;
  ip: string | null;
}

export interface GroupPerson {
  username: string;
  displayName: string;
  department: string;
  inviteByDefault: boolean;
  isGroupAdmin: boolean;
}

export interface GroupRequest {
  id: string;
  kind: 'trade' | 'internal';
  name: string;
  title: string;
  departmentKey: string | null;
  categoryKey: string | null;
  members: string[];
  bots: string[];
  extra: string[];
  admins: string[];
  options?: {
    postCreateMessages?: string[];
    autoDeleteSec?: number;
    sendInviteLink?: boolean;
    pingExtraMembers?: boolean;
    chatCategory?: string | null;
    /** Контрагент в TradeBook (торговая группа). */
    crmClient?: { organizationId: number } | null;
  };
  status: GroupRequestStatus;
  chatId: string | null;
  inviteLink: string | null;
  result: {
    steps?: string[];
    invited?: string[];
    failed?: { username: string | null; error: string }[];
    promoted?: string[];
    /** Ограничение Telegram на приглашения: until — до какого времени (null — без срока, например PEER_FLOOD). */
    flood?: { error: string; seconds: number | null; until: string | null; at: string; legacy?: boolean } | null;
    /** Кого не пригласили из-за ограничения — «Пригласить оставшихся». */
    pendingInvites?: string[];
    /** Контрагент TradeBook: id созданного или текст ошибки. */
    crmClientId?: number | null;
    crmError?: string | null;
  };
  error: string | null;
  attempts: number;
  requestedBy: string | null;
  requestedByLogin: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface AnalyticsSummary {
  period: { from: string; to: string; timezone: string; slaSec: number };
  totals: {
    total: number;
    open: number;
    abandoned: number;
    promisesBroken: number;
    taken: number;
    excluded: number;
    avgResponseSec: number | null;
    medianResponseSec: number | null;
    p90ResponseSec: number | null;
    medianResolutionSec: number | null;
    slaRate: number | null;
    amountUsd: number | null;
    closed: number;
    /** Ответили в пределах SLA / из скольких (с ответом или закрытых). */
    withinSla: number;
    slaBase: number;
    /** Закрыты неудачно (мимо, клиент ушёл), без «Брошен». */
    lost: number;
    /** Карточек с распознанной суммой. */
    withAmount: number;
    /** Успешно закрытые: положительные статусы (ответили, сделка, зафиксировали, решено вне чата). */
    deals: number;
    /** Запросы для конверсии — без скипа и дублей. */
    conversionBase: number;
    /** Конверсия: deals / conversionBase. */
    conversion: number | null;
  };
  byResolution: { key: string | null; count: number; excludedFromSla: boolean }[];
  byColumn: { key: string; count: number; abandoned: number; medianResponseSec: number | null }[];
  byEmployee: AnalyticsEmployeeRow[];
  byHour: { hour: number; count: number; abandoned: number; medianResponseSec: number | null }[];
  byDay: { day: string; count: number; abandoned: number; medianResponseSec: number | null }[];
  /** Распределение первых ответов по скорости — без учёта фильтра скорости. */
  byResponse: { answered: number; buckets: ResponseBuckets };
  /** По отделам — засчитанные ответы сотрудников отдела (с личными колонками). */
  byDepartment: (Omit<AnalyticsEmployeeRow, 'id' | 'name' | 'department' | 'closed'> & { key: string; people: number })[];
  /** Сотрудники без закреплённых и без личных колонок — их ответы не засчитываются. */
  unassigned: { id: number; name: string }[];
  response: ResponseBucketKey | null;
}

export type AnalyticsQuery = {
  from: string;
  to: string;
  column?: string;
  /** Группа колонок (id) — сводка по её колонкам. */
  group?: number;
  response?: ResponseBucketKey;
};

export type ResponseBuckets = Record<ResponseBucketKey, number>;

export interface AnalyticsEmployeeRow {
  id: number;
  name: string;
  department: string;
  /** Засчитанных ответов: первая реакция по карточке своей колонки (у личных колонок — слово закрытия). */
  count: number;
  /** Закрыл карточек своих колонок (не обязательно тех, где ответил первым). */
  closed: number;
  medianResponseSec: number | null;
  avgResponseSec: number | null;
  p90ResponseSec: number | null;
  slaRate: number | null;
  buckets: ResponseBuckets;
}

export interface AnalyticsEmployeeDetail {
  employee: { id: number; name: string; department: string };
  period: { from: string; to: string; timezone: string; slaSec: number; unit: 'hour' | 'day' };
  response: ResponseBucketKey | null;
  totals: Omit<AnalyticsEmployeeRow, 'id' | 'name' | 'department'>;
  /** Распределение по скорости без учёта фильтра скорости. */
  distribution: ResponseBuckets;
  series: { slot: string; count: number; closed: number; medianResponseSec: number | null; slaRate: number | null; buckets: ResponseBuckets }[];
  tickets: {
    id: string;
    chatId: string;
    chatTitle: string;
    columnKey: string;
    state: string;
    resolutionKey: string | null;
    firstMessageAt: string;
    /** Начало отсчёта: попадание в колонку (вопрос, начало рабочего дня или перенос). */
    startAt: string;
    firstResponseAt: string | null;
    firstResponseSec: number | null;
    /** Ответил первым. */
    responded: boolean;
    closedByMe: boolean;
    closedAt: string | null;
  }[];
}

export interface BroadcastChat {
  id: string;
  title: string | null;
  chatType: string | null;
}
export interface BroadcastList {
  id: string;
  name: string;
  chats: BroadcastChat[];
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface Broadcast {
  id: string;
  listId: string | null;
  listName: string;
  text: string;
  createdBy: string | null;
  createdAt: string;
  lastSentAt: string | null;
  total: number;
  sent: number;
  queued: number;
  failed: number;
  status: 'sending' | 'done' | 'partial' | 'error';
}
export interface BroadcastDelivery {
  chatId: string;
  title: string | null;
  status: 'pending' | 'sending' | 'sent' | 'error' | 'expired';
  error: string | null;
  sentAt: string | null;
}

export interface InviteDto {
  id: string;
  chatId: string;
  chatTitle: string | null;
  username: string;
  status: 'pending' | 'running' | 'done' | 'error';
  result: Record<string, unknown>;
  error: string | null;
  requestedBy: string | null;
  requestedByLogin: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export type RefName = 'columns' | 'categories' | 'resolutions' | 'departments';

export const api = {
  auth: {
    me: () => http.get<{ user: SessionUser }>('/api/auth/me'),
    login: (login: string, password: string) => http.post<{ user: SessionUser }>('/api/auth/login', { login, password }),
    logout: () => http.post('/api/auth/logout'),
    changePassword: (current: string, next: string) => http.post('/api/auth/password', { current, next }),
  },
  bootstrap: () => http.get<Bootstrap>('/api/bootstrap'),
  prefs: {
    get: () => http.get<{ prefs: UserPrefs; saved: boolean }>('/api/me/prefs'),
    save: (prefs: UserPrefs) => http.put<{ prefs: UserPrefs }>('/api/me/prefs', prefs),
  },
  engineStatus: () => http.get<{ status: Bootstrap['engine'] }>('/api/engine/status'),

  board: () => http.get<{ tickets: TicketDto[] }>('/api/board'),
  tickets: {
    list: (q: Record<string, string | number | undefined>) => http.get<{ tickets: TicketDto[] }>('/api/tickets', q),
    get: (id: string) =>
      http.get<{ ticket: TicketDto; events: TicketEventDto[]; messages: ChatMessageDto[]; replies: OutgoingReplyDto[]; marks?: TicketMarks }>(
        `/api/tickets/${id}`,
      ),
    reply: (id: string, text: string, replyToMsgId?: number | null) =>
      http.post<{ ticket: TicketDto }>(`/api/tickets/${id}/reply`, { text, replyToMsgId: replyToMsgId ?? null }),
    watch: (id: string) => http.post<{ ok: true } | { ok: false; watcher: TicketWatcher }>(`/api/tickets/${id}/watch`),
    unwatch: (id: string) => http.del<{ ok: true }>(`/api/tickets/${id}/watch`),
    mentions: (minutes: number) => http.get<{ username: string | null; items: MentionDto[] }>('/api/me/mentions', { minutes }),
    context: (chatId: string, messageId: number) =>
      http.get<{ messages: ChatMessageDto[] }>(`/api/chats/${chatId}/context`, { messageId }),
    watchers: () => http.get<{ items: { ticketId: string; watcher: TicketWatcher }[] }>('/api/watchers'),
    take: (id: string) => http.post<{ ticket: TicketDto }>(`/api/tickets/${id}/take`),
    release: (id: string) => http.post<{ ticket: TicketDto }>(`/api/tickets/${id}/release`),
    resolve: (id: string, resolutionKey: string, comment?: string | null) =>
      http.post<{ ticket: TicketDto }>(`/api/tickets/${id}/resolve`, { resolutionKey, comment: comment || null }),
    move: (id: string, columnKey: string, comment?: string | null) =>
      http.post<{ ticket: TicketDto }>(`/api/tickets/${id}/move`, { columnKey, comment: comment || null }),
    comment: (id: string, text: string) => http.post<{ ticket: TicketDto }>(`/api/tickets/${id}/comment`, { text }),
    reopen: (id: string) => http.post<{ ticket: TicketDto }>(`/api/tickets/${id}/reopen`),
  },

  chats: {
    list: (q: Record<string, string | number | undefined>) => http.get<{ items: ChatDto[]; total: number }>('/api/chats', q),
    setProfile: (
      id: string,
      profile: { categoryKey: string | null; trackingOverride: boolean | null; note?: string | null; closeOpenAs?: string | null },
    ) => http.put<{ chat: ChatDto }>(`/api/chats/${id}/profile`, profile),
    pin: (id: string, columnKeys: string[], moveOpen = true) =>
      http.put<{ chat: ChatDto; moved: number; merged: number }>(`/api/chats/${id}/pin`, { columnKeys, moveOpen }),
    bulkProfile: (
      chatIds: string[],
      profile: { categoryKey: string | null; trackingOverride: boolean | null; closeOpenAs?: string | null },
    ) =>
      http.post<{ updated: number }>('/api/chats/bulk-profile', { chatIds, profile }),
  },

  broadcasts: {
    lists: () => http.get<{ items: BroadcastList[] }>('/api/broadcast-lists'),
    createList: (body: { name: string; chatIds: string[] }) => http.post<{ item: BroadcastList }>('/api/broadcast-lists', body),
    updateList: (id: string, body: { name: string; chatIds: string[] }) => http.put<{ item: BroadcastList }>(`/api/broadcast-lists/${id}`, body),
    removeList: (id: string) => http.del(`/api/broadcast-lists/${id}`),
    history: () => http.get<{ items: Broadcast[] }>('/api/broadcasts'),
    send: (body: { listId: string; text: string }) => http.post<{ item: Broadcast }>('/api/broadcasts', body),
    deliveries: (id: string) => http.get<{ items: BroadcastDelivery[] }>(`/api/broadcasts/${id}/deliveries`),
    retry: (id: string) => http.post<{ item: Broadcast }>(`/api/broadcasts/${id}/retry`),
  },
  groups: {
    config: () => http.get<{ settings: Settings['groups']; staff: GroupPerson[]; bots: GroupPerson[] }>('/api/groups/config'),
    list: () => http.get<{ requests: GroupRequest[] }>('/api/groups'),
    get: (id: string) => http.get<{ request: GroupRequest }>(`/api/groups/${id}`),
    create: (body: {
      kind: 'trade' | 'internal';
      name: string;
      departmentKey?: string;
      categoryKey?: string;
      members: string[];
      bots: string[];
      extra: string[];
    }) => http.post<{ request: GroupRequest }>('/api/groups', body),
    cancel: (id: string) => http.post<{ request: GroupRequest }>(`/api/groups/${id}/cancel`),
    retry: (id: string) => http.post<{ request: GroupRequest }>(`/api/groups/${id}/retry`),
  },

  analytics: (q: AnalyticsQuery) => http.get<AnalyticsSummary>('/api/analytics/summary', q),
  analyticsEmployee: (id: number, q: AnalyticsQuery) => http.get<AnalyticsEmployeeDetail>(`/api/analytics/employees/${id}`, q),

  settings: {
    get: () =>
      http.get<{ sections: SettingsSection[]; values: Settings; versions: Record<SettingsSection, number> }>('/api/settings'),
    save: <K extends SettingsSection>(section: K, value: Settings[K], version: number) =>
      http.put<{ value: Settings[K]; version: number }>(`/api/settings/${section}`, { value, version }),
    reset: <K extends SettingsSection>(section: K, version: number) =>
      http.post<{ value: Settings[K]; version: number }>(`/api/settings/${section}/reset`, { version }),
  },

  ref: {
    list: <T>(ref: RefName) => http.get<{ items: T[] }>(`/api/ref/${ref}`),
    create: <T>(ref: RefName, item: T) => http.post<{ item: T }>(`/api/ref/${ref}`, item),
    update: <T>(ref: RefName, key: string, item: T) => http.put<{ item: T }>(`/api/ref/${ref}/${key}`, item),
    remove: (ref: RefName, key: string) => http.del(`/api/ref/${ref}/${key}`),
  },

  employees: {
    list: () => http.get<{ items: EmployeeFull[] }>('/api/employees'),
    create: (e: Omit<EmployeeFull, 'id' | 'active'>) => http.post<{ item: EmployeeFull }>('/api/employees', e),
    update: (id: number, e: Omit<EmployeeFull, 'id' | 'active'>) => http.put<{ item: EmployeeFull }>(`/api/employees/${id}`, e),
    archive: (id: number) => http.post<{ item: EmployeeFull }>(`/api/employees/${id}/archive`),
    restore: (id: number) => http.post<{ item: EmployeeFull }>(`/api/employees/${id}/restore`),
    lookup: (username: string) =>
      http.get<{ matches: { telegramUserId: string; username: string; name: string | null }[] }>('/api/employees/lookup', { username }),
  },

  users: {
    list: () => http.get<{ items: AppUserWithAccess[] }>('/api/users'),
    create: (u: { login: string; displayName: string; role: UserRole; password: string; employeeId?: number | null; access?: ColumnAccess }) =>
      http.post<{ item: AppUser }>('/api/users', u),
    setAccess: (id: number, access: ColumnAccess) => http.put<{ access: ColumnAccess }>(`/api/users/${id}/access`, access),
    update: (id: number, u: { displayName: string; role: UserRole; active: boolean; employeeId?: number | null }) =>
      http.put<{ item: AppUser }>(`/api/users/${id}`, u),
    resetPassword: (id: number, password: string) => http.post(`/api/users/${id}/password`, { password }),
  },

  /** Личные колонки текущего пользователя. */
  personalColumns: {
    list: () => http.get<{ items: (PersonalColumn & { owner: PersonalOwner | null })[] }>('/api/personal-columns'),
    update: (key: string, c: PersonalColumnInput) => http.put<{ item: PersonalColumn }>(`/api/personal-columns/${key}`, c),
  },
  myColumns: {
    list: () => http.get<{ items: PersonalColumn[]; me: PersonalOwner | null }>('/api/my/columns'),
    create: (c: PersonalColumnInput) => http.post<{ item: PersonalColumn }>('/api/my/columns', c),
    update: (key: string, c: PersonalColumnInput) => http.put<{ item: PersonalColumn }>(`/api/my/columns/${key}`, c),
    remove: (key: string) => http.del(`/api/my/columns/${key}`),
  },

  columnGroups: {
    list: () => http.get<{ items: ColumnGroupFull[] }>('/api/column-groups'),
    create: (g: ColumnGroupInput) => http.post<{ item: ColumnGroupFull }>('/api/column-groups', g),
    update: (id: number, g: ColumnGroupInput) => http.put<{ item: ColumnGroupFull }>(`/api/column-groups/${id}`, g),
    remove: (id: number) => http.del(`/api/column-groups/${id}`),
  },

  autoreplyLog: () => http.get<{ items: AutoreplyLogItem[] }>('/api/autoreply/log'),
  /** Пересчитать нерабочее время карточек колонок со своим расписанием. */
  autoreplyRecompute: () => http.post<{ checked: number; updated: number }>('/api/settings/autoreply/recompute'),

  columnStats: (key: string, q: { from: string; to: string }) => http.get<PersonalColumnStats>(`/api/columns/${key}/stats`, q),

  columnAccess: {
    get: (key: string) => http.get<ColumnAccessByColumn>(`/api/columns/${key}/access`),
    set: (key: string, a: ColumnAccessByColumn) => http.put<ColumnAccessByColumn>(`/api/columns/${key}/access`, a),
  },

  invites: {
    /** Журнал всех заявок — только администратору. */
    list: () => http.get<{ invites: InviteDto[] }>('/api/invites'),
    mine: () => http.get<{ invites: InviteDto[] }>('/api/invites/mine'),
    create: (chatId: string, username: string) => http.post<{ invite: InviteDto }>('/api/invites', { chatId, username }),
  },

  audit: (q: Record<string, string | number | undefined>) => http.get<{ items: AuditItem[] }>('/api/audit', q),
};
